/**
 * Who is signed in, what they may do, which language they read. Loads /auth/me once, exposes
 * `can(permission)` built on the same PermissionSet the server uses, and switches locale on the
 * server so printed documents and e-mails follow the user's choice.
 */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError, onUnauthenticated } from './api.ts';
import { PermissionSet } from '../../core/auth/permissions.ts';
import type { Locale } from '../../core/i18n/format.ts';

export interface SessionSummary {
  user: { id: string; email: string; fullName: string; locale: Locale; timezone: string; avatarFileId: string | null };
  tenant: { id: string; name: string; slug: string; branding: Record<string, unknown> } | null;
  memberships: Array<{ tenantId: string; tenantName: string; slug: string }>;
  companyId: string | null; employeeId: string | null; jobTitle: string | null; isOwner: boolean;
  roles: Array<{ code: string; name: string; nameAr: string | null }>;
  granted: string[]; sensitivePermissions: string[];
  /** the authorization matrix as resolved for this user (screens / reports / functions) and the data scopes */
  access: { allow: Record<'screen' | 'report' | 'function', string[]>; deny: Record<'screen' | 'report' | 'function', string[]>; everything: boolean } | null;
  scopes: Partial<Record<'company' | 'branch' | 'project' | 'warehouse' | 'cost_center', string[] | null>>;
}

interface SessionApi {
  session: SessionSummary | null;
  loading: boolean;
  locale: Locale;
  can: (permission: string) => boolean;
  canAny: (permissions: string[]) => boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  setLocale: (locale: Locale) => Promise<void>;
  reload: () => Promise<void>;
}

const SessionContext = createContext<SessionApi | null>(null);
const LOCALE_KEY = 'wc.locale';

function readStoredLocale(): Locale | null {
  try { const v = localStorage.getItem(LOCALE_KEY); return v === 'ar' || v === 'en' ? v : null; } catch { return null; }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<SessionSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [locale, setLocaleState] = useState<Locale>(() => readStoredLocale() ?? (navigator.language.startsWith('ar') ? 'ar' : 'en'));

  const apply = useCallback((s: SessionSummary | null) => {
    setSession(s);
    if (s) { setLocaleState(s.user.locale); try { localStorage.setItem(LOCALE_KEY, s.user.locale); } catch { /* private mode */ } }
  }, []);

  const reload = useCallback(async () => {
    try { apply(await api.get<SessionSummary>('auth/me')); }
    catch (e) { if (e instanceof ApiError && e.status === 401) setSession(null); else throw e; }
    finally { setLoading(false); }
  }, [apply]);

  useEffect(() => { void reload(); }, [reload]);
  useEffect(() => onUnauthenticated(() => setSession(null)), []);
  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = locale === 'ar' ? 'rtl' : 'ltr';
  }, [locale]);

  const perms = useMemo(() => new PermissionSet(session?.granted ?? [], (session?.sensitivePermissions ?? []).map((code) => ({ code, module: '', entity: '', action: 'view' as const, sensitive: true }))), [session]);
  const value = useMemo<SessionApi>(() => ({
    session, loading, locale,
    can: (p) => perms.has(p),
    canAny: (ps) => ps.some((p) => perms.has(p)),
    login: async (email, password) => { apply(await api.post<SessionSummary>('auth/login', { email, password })); },
    logout: async () => { try { await api.post('auth/logout'); } finally { setSession(null); } },
    setLocale: async (l) => {
      setLocaleState(l); try { localStorage.setItem(LOCALE_KEY, l); } catch { /* ignore */ }
      if (session) { await api.post('auth/locale', { locale: l }); setSession({ ...session, user: { ...session.user, locale: l } }); }
    },
    reload,
  }), [session, loading, locale, perms, apply, reload]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionApi {
  const v = useContext(SessionContext);
  if (!v) throw new Error('useSession outside SessionProvider');
  return v;
}
